import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import {
  FileText,
  Layers,
  CheckCircle2,
  XCircle,
  ReceiptText,
  Hourglass,
  CircleDollarSign,
  TrendingUp,
} from "lucide-react";

const PERIODS = [
  { value: "today", label: "Hari ini" },
  { value: "week", label: "Pekan ini" },
  { value: "month", label: "Bulan ini" },
  { value: "year", label: "Tahun ini" },
  { value: "all", label: "Seluruhnya" },
  { value: "custom", label: "Rentang…" },
];

const TONES = {
  ink: { card: "bg-white border-border", chip: "bg-ink-900 text-white", strip: "from-ink-900 via-ink-700 to-ink-500", value: "text-ink-900" },
  sph: { card: "bg-gradient-to-br from-sky-50 to-sky-100/40 border-sky-200", chip: "bg-sky-500 text-white", strip: "from-sky-500 via-sky-400 to-sky-300", value: "text-sky-900" },
  invoice: { card: "bg-gradient-to-br from-amber-50 to-amber-100/40 border-amber-200", chip: "bg-amber-500 text-white", strip: "from-amber-500 via-amber-400 to-amber-300", value: "text-amber-900" },
  success: { card: "bg-gradient-to-br from-emerald-50 to-emerald-100/40 border-emerald-200", chip: "bg-emerald-500 text-white", strip: "from-emerald-500 via-emerald-400 to-emerald-300", value: "text-emerald-900" },
  danger: { card: "bg-gradient-to-br from-rose-50 to-rose-100/40 border-rose-200", chip: "bg-rose-500 text-white", strip: "from-rose-500 via-rose-400 to-rose-300", value: "text-rose-900" },
};

function StatCard({ label, value, icon: Icon, tone = "ink", testId }) {
  const t = TONES[tone];
  return (
    <Card
      className={`group relative overflow-hidden p-5 border shadow-none transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${t.card}`}
      data-testid={testId}
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.strip} opacity-90`} />
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="overline">{label}</p>
          <div className={`mt-3 font-heading text-2xl font-bold tracking-tight ${t.value}`}>{value}</div>
        </div>
        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${t.chip}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function StatsPanel({
  scope = "all",
  title = "Statistik",
  subtitle,
  testIdPrefix = "stats",
  accentTone = "brand", // "brand" | "sph"
}) {
  const [period, setPeriod] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stats, setStats] = useState({
    total: 0, nominal: 0, nominal_sph: 0, nominal_invoice: 0,
    sph: 0, invoice: 0, pengajuan: 0, selesai: 0, batal: 0,
  });
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, scope };
      if (period === "custom") {
        if (!customStart || !customEnd) {
          setLoading(false);
          return;
        }
        params.start = customStart;
        params.end = customEnd;
      }
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
    } catch (e) {
      // Non-fatal; keep zeros
      console.warn("stats reload failed:", formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [period, scope, customStart, customEnd]);

  useEffect(() => { reload(); }, [reload]);

  const heroClass = useMemo(() => {
    if (accentTone === "sph") return "bg-gradient-to-br from-sky-500 to-sky-700";
    if (accentTone === "invoice") return "bg-gradient-to-br from-amber-500 to-amber-600";
    return "bg-gradient-to-br from-brand to-brand-700";
  }, [accentTone]);

  return (
    <section className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      {/* Title + Period Selector */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="overline">{title}</p>
          {subtitle && <p className="mt-1 text-sm text-ink-500">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 rounded-full border border-border bg-card p-1 shadow-sm">
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              size="sm"
              variant={period === p.value ? "default" : "ghost"}
              onClick={() => setPeriod(p.value)}
              className={`h-8 rounded-full px-3 text-xs ${period === p.value ? "bg-ink-900 text-white hover:bg-ink-900" : "text-ink-500 hover:bg-secondary"}`}
              data-testid={`${testIdPrefix}-period-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Custom range picker */}
      {period === "custom" && (
        <div className="grid grid-cols-1 gap-3 rounded-md border border-dashed border-border bg-secondary/30 p-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Tanggal mulai</Label>
            <DatePicker value={customStart} onChange={setCustomStart} placeholder="Tanggal awal" testId={`${testIdPrefix}-custom-start`} />
          </div>
          <div>
            <Label className="text-xs">Tanggal akhir</Label>
            <DatePicker value={customEnd} onChange={setCustomEnd} placeholder="Tanggal akhir" testId={`${testIdPrefix}-custom-end`} />
          </div>
        </div>
      )}

      {/* Hero Total Nominal */}
      <Card className={`relative overflow-hidden border-transparent p-6 text-white shadow-none ${heroClass}`} data-testid={`${testIdPrefix}-nominal-total`}>
        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 -left-8 h-40 w-40 rounded-full bg-white/5" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="overline text-white/70">Total Nominal Transaksi</p>
            <div className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {loading ? "…" : formatRupiah(stats.nominal)}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-white/80">
              <TrendingUp className="h-3.5 w-3.5" /> {stats.total} surat · {PERIODS.find((p) => p.value === period)?.label}
            </div>
          </div>
          <div className="grid h-14 w-14 place-items-center rounded-xl bg-white/15 ring-1 ring-white/25">
            <CircleDollarSign className="h-7 w-7" />
          </div>
        </div>
      </Card>

      {/* Nominal per Jenis */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          testId={`${testIdPrefix}-nominal-sph`}
          label="Nominal SPH"
          value={formatRupiah(stats.nominal_sph)}
          icon={Layers}
          tone="sph"
        />
        <StatCard
          testId={`${testIdPrefix}-nominal-invoice`}
          label="Nominal Invoice"
          value={formatRupiah(stats.nominal_invoice)}
          icon={ReceiptText}
          tone="invoice"
        />
      </div>

      {/* Jumlah + Status */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard testId={`${testIdPrefix}-total`} label="Total Surat" value={stats.total} icon={FileText} tone="ink" />
        <StatCard testId={`${testIdPrefix}-sph`} label="Jumlah SPH" value={stats.sph} icon={Layers} tone="sph" />
        <StatCard testId={`${testIdPrefix}-invoice`} label="Jumlah Invoice" value={stats.invoice} icon={ReceiptText} tone="invoice" />
        <StatCard testId={`${testIdPrefix}-pengajuan`} label="Pengajuan" value={stats.pengajuan} icon={Hourglass} tone="invoice" />
        <StatCard testId={`${testIdPrefix}-selesai`} label="Selesai" value={stats.selesai} icon={CheckCircle2} tone="success" />
      </div>

      {/* Single Batal card (separated so it doesn't overload status row) */}
      <div className="grid grid-cols-1">
        <StatCard testId={`${testIdPrefix}-batal`} label="Surat Dibatalkan" value={stats.batal} icon={XCircle} tone="danger" />
      </div>
    </section>
  );
}
